import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import { resetUserPasswordFormSchema, ResetUserPasswordFormValues } from '../../validation/user';
import { PasswordStrengthMeter } from '../../components/PasswordStrengthMeter';

type Mode = 'manual' | 'generate';

interface ResetUserPasswordDialogProps {
  open: boolean;
  userName: string;
  onClose: () => void;
  onSubmit: (payload: { newPassword: string } | { generateTemporary: true }) => Promise<{ temporaryPassword?: string } | null>;
}

export const ResetUserPasswordDialog = ({ open, userName, onClose, onSubmit }: ResetUserPasswordDialogProps) => {
  const [mode, setMode] = useState<Mode>('generate');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ResetUserPasswordFormValues>({ resolver: zodResolver(resetUserPasswordFormSchema) });

  const newPassword = watch('newPassword');

  const handleClose = () => {
    setMode('generate');
    setErrorMessage(null);
    setTemporaryPassword(null);
    setCopied(false);
    reset();
    onClose();
  };

  const handleGenerate = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const result = await onSubmit({ generateTemporary: true });
      setTemporaryPassword(result?.temporaryPassword ?? null);
    } catch {
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManual = async (values: ResetUserPasswordFormValues) => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await onSubmit({ newPassword: values.newPassword });
      handleClose();
    } catch {
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!temporaryPassword) return;
    await navigator.clipboard.writeText(temporaryPassword);
    setCopied(true);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset password for {userName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {temporaryPassword ? (
            <Alert severity="success">
              <Typography variant="body2" sx={{ mb: 1 }}>
                Temporary password generated. This is shown once — share it with the user securely.
                They&apos;ll be required to change it on next login.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={temporaryPassword}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                />
                <Tooltip title={copied ? 'Copied' : 'Copy'}>
                  <IconButton size="small" onClick={handleCopy}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Alert>
          ) : (
            <>
              <ToggleButtonGroup
                value={mode}
                exclusive
                fullWidth
                onChange={(_e, value: Mode | null) => value && setMode(value)}
              >
                <ToggleButton value="generate">Generate temporary password</ToggleButton>
                <ToggleButton value="manual">Set a specific password</ToggleButton>
              </ToggleButtonGroup>

              {mode === 'generate' ? (
                <Typography variant="body2" color="text.secondary">
                  A strong random password will be generated and shown once. The user must change it
                  on their next login.
                </Typography>
              ) : (
                <Box component="form" onSubmit={handleSubmit(handleManual)} noValidate>
                  <Stack spacing={2}>
                    <TextField
                      label="New password"
                      type="password"
                      fullWidth
                      error={!!errors.newPassword}
                      helperText={errors.newPassword?.message}
                      {...register('newPassword')}
                    />
                    <PasswordStrengthMeter password={newPassword ?? ''} />
                    <TextField
                      label="Confirm password"
                      type="password"
                      fullWidth
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      {...register('confirmPassword')}
                    />
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{temporaryPassword ? 'Done' : 'Cancel'}</Button>
        {!temporaryPassword &&
          (mode === 'generate' ? (
            <Button variant="contained" disabled={submitting} onClick={handleGenerate}>
              {submitting ? 'Resetting…' : 'Generate & reset'}
            </Button>
          ) : (
            <Button variant="contained" disabled={submitting} onClick={handleSubmit(handleManual)}>
              {submitting ? 'Resetting…' : 'Reset password'}
            </Button>
          ))}
      </DialogActions>
    </Dialog>
  );
};
